import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const EMPTY_DEPT = { code: "", name: "", slug: "", seo_text: "", meta_title: "", meta_description: "", h1: "", active: true };
const EMPTY_CITY = { city: "", slug: "", department_code: "", seo_text: "", meta_title: "", meta_description: "", h1: "", active: true };

export default function AdminZones() {
  const [tab, setTab] = useState("dept");

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">Zones — Départements & villes</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-6" />
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("dept")} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border ${tab === "dept" ? "bg-brand text-white border-brand" : "border-ink-700 text-zinc-400 hover:border-brand"}`}>Départements</button>
        <button onClick={() => setTab("city")} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border ${tab === "city" ? "bg-brand text-white border-brand" : "border-ink-700 text-zinc-400 hover:border-brand"}`}>Villes</button>
      </div>
      {tab === "dept" ? <DepartmentsPanel /> : <CitiesPanel />}
      <style>{`.input-admin{ width:100%; height:40px; padding:0 12px; background:#3C3C40; border:1px solid #4A4A4D; color:#fff; font-size:14px; outline:none; } .input-admin:focus{ border-color:#FF6200;} .input-admin.textarea{ height:auto; padding:10px 12px; }`}</style>
    </div>
  );
}

function DepartmentsPanel() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DEPT);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/departments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.code.trim() || !draft.name.trim()) return toast.error("Code et nom requis");
    try { await api.post("/admin/departments", draft); setDraft(EMPTY_DEPT); load(); toast.success("Département ajouté"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const save = async (d) => {
    try { await api.patch(`/admin/departments/${d.slug}`, d); setEditing(null); load(); toast.success("Mis à jour"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Supprimer ce département ?")) return;
    try { await api.delete(`/admin/departments/${slug}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="bg-ink-900 border border-ink-700 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Nouveau département</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input className="input-admin" placeholder="Code (ex: 75)" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
          <input className="input-admin" placeholder="Nom" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input className="input-admin" placeholder="Slug (auto si vide)" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
          <button onClick={create} className="bg-brand text-white px-4 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm"><Plus size={14} /> Ajouter</button>
        </div>
      </div>

      <div className="grid gap-3">
        {rows.map((d) => (
          <div key={d.slug} className="bg-ink-900 border border-ink-700 p-4">
            {editing === d.slug ? <EditDept row={d} onSave={save} onCancel={() => setEditing(null)} /> : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-black text-brand text-2xl">{d.code}</span>
                    <span className="font-bold">{d.name}</span>
                    <span className="text-zinc-500 text-xs">/{d.slug}</span>
                    {!d.active && <span className="text-xs text-red-500 font-bold">MASQUÉ</span>}
                  </div>
                  <div className="text-zinc-400 text-sm mt-1 line-clamp-2">{d.seo_text}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(d.slug)} className="text-zinc-400 hover:text-brand text-xs font-bold uppercase">Modifier</button>
                  <button onClick={() => remove(d.slug)} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <div className="text-zinc-500 text-center py-8">Aucun département</div>}
      </div>
    </div>
  );
}

function EditDept({ row, onSave, onCancel }) {
  const [r, setR] = useState(row);
  return (
    <div className="grid gap-2">
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="input-admin" placeholder="Code" value={r.code} onChange={(e) => setR({ ...r, code: e.target.value })} />
        <input className="input-admin" placeholder="Nom" value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} />
        <label className="text-sm text-zinc-300 inline-flex items-center gap-2"><input type="checkbox" checked={!!r.active} onChange={(e) => setR({ ...r, active: e.target.checked })} /> Actif</label>
      </div>
      <input className="input-admin" placeholder="H1" value={r.h1 || ""} onChange={(e) => setR({ ...r, h1: e.target.value })} />
      <input className="input-admin" placeholder="Meta title" value={r.meta_title || ""} onChange={(e) => setR({ ...r, meta_title: e.target.value })} />
      <textarea className="input-admin textarea" rows="2" placeholder="Meta description" value={r.meta_description || ""} onChange={(e) => setR({ ...r, meta_description: e.target.value })} />
      <textarea className="input-admin textarea" rows="4" placeholder="Texte SEO de la page" value={r.seo_text || ""} onChange={(e) => setR({ ...r, seo_text: e.target.value })} />
      <div className="flex gap-2">
        <button onClick={() => onSave(r)} className="bg-brand text-white px-4 py-2 text-sm font-bold uppercase inline-flex items-center gap-2"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="bg-ink-800 text-white px-4 py-2 text-sm font-bold uppercase">Annuler</button>
      </div>
    </div>
  );
}

function CitiesPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState(EMPTY_CITY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/cities").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.city.trim() || !draft.department_code.trim()) return toast.error("Ville et département requis");
    try { await api.post("/admin/cities", draft); setDraft(EMPTY_CITY); load(); toast.success("Ville ajoutée"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const save = async (c) => {
    try { await api.patch(`/admin/cities/${c.slug}`, c); setEditing(null); load(); toast.success("Mis à jour"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Supprimer cette ville ?")) return;
    try { await api.delete(`/admin/cities/${slug}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = rows.filter((c) => !filter || c.city.toLowerCase().includes(filter.toLowerCase()) || (c.department_code || "").includes(filter));

  return (
    <div>
      <div className="bg-ink-900 border border-ink-700 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Nouvelle ville</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input className="input-admin" placeholder="Ville" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          <input className="input-admin" placeholder="Département (75, 93…)" value={draft.department_code} onChange={(e) => setDraft({ ...draft, department_code: e.target.value })} />
          <input className="input-admin" placeholder="Slug (auto)" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
          <button onClick={create} className="bg-brand text-white px-4 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm"><Plus size={14} /> Ajouter</button>
        </div>
      </div>

      <input className="input-admin mb-3" placeholder="Filtrer (ville ou département)" value={filter} onChange={(e) => setFilter(e.target.value)} />

      <div className="grid gap-2">
        {filtered.map((c) => (
          <div key={c.slug} className="bg-ink-900 border border-ink-700 p-3">
            {editing === c.slug ? <EditCity row={c} onSave={save} onCancel={() => setEditing(null)} /> : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{c.city}</span>
                    <span className="text-brand text-xs font-bold">{c.department_code}</span>
                    <span className="text-zinc-500 text-xs">/{c.slug}</span>
                    {!c.active && <span className="text-xs text-red-500 font-bold">MASQUÉ</span>}
                  </div>
                  <div className="text-zinc-400 text-xs mt-1 line-clamp-1">{c.seo_text}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(c.slug)} className="text-zinc-400 hover:text-brand text-xs font-bold uppercase">Modifier</button>
                  <button onClick={() => remove(c.slug)} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!filtered.length && <div className="text-zinc-500 text-center py-8">Aucune ville</div>}
      </div>
    </div>
  );
}

function EditCity({ row, onSave, onCancel }) {
  const [r, setR] = useState(row);
  return (
    <div className="grid gap-2">
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="input-admin" value={r.city} onChange={(e) => setR({ ...r, city: e.target.value })} />
        <input className="input-admin" value={r.department_code} onChange={(e) => setR({ ...r, department_code: e.target.value })} />
        <label className="text-sm text-zinc-300 inline-flex items-center gap-2"><input type="checkbox" checked={!!r.active} onChange={(e) => setR({ ...r, active: e.target.checked })} /> Actif</label>
      </div>
      <input className="input-admin" placeholder="H1" value={r.h1 || ""} onChange={(e) => setR({ ...r, h1: e.target.value })} />
      <input className="input-admin" placeholder="Meta title" value={r.meta_title || ""} onChange={(e) => setR({ ...r, meta_title: e.target.value })} />
      <textarea className="input-admin textarea" rows="2" placeholder="Meta description" value={r.meta_description || ""} onChange={(e) => setR({ ...r, meta_description: e.target.value })} />
      <textarea className="input-admin textarea" rows="4" placeholder="Texte SEO" value={r.seo_text || ""} onChange={(e) => setR({ ...r, seo_text: e.target.value })} />
      <div className="flex gap-2">
        <button onClick={() => onSave(r)} className="bg-brand text-white px-4 py-2 text-sm font-bold uppercase inline-flex items-center gap-2"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="bg-ink-800 text-white px-4 py-2 text-sm font-bold uppercase">Annuler</button>
      </div>
    </div>
  );
}
